/**
 * The form of one named secret (the Secrets page). The type says what the secret is,
 * where one comes from, who may use it and how it is secured; the form shows that under
 * the Type field. The value is entered here and never shown again: editing a stored
 * secret with the value left blank changes its label and hosts and keeps the value.
 */
import type { FormItem } from "./form-builder.model";
import { datasetFormRequest } from "./dataset-form-signal";
import { notify } from "../notifications/NotificationProvider";
import { ViewManager } from "../../services/view-manager";
import {
  listSecrets,
  listSecretTypes,
  parseHosts,
  putSecret,
  type Secret,
  type SecretType,
} from "../../services/secret-admin-rpc-client";

export type SecretFormOpts = {
  /** "<type>/<name>" of the secret to edit; undefined = new */
  key?: string;
  /** a view to re-fetch after a save (the page's list) */
  refreshView?: string;
  /** for a new secret: the type to start on, e.g. when a channel form asks for a slack-webhook */
  kind?: string;
};

export const secretKey = (kind: string, name: string): string => `${kind}/${name}`;

/** What a type says about every secret of that type, as the lines shown under the form's title. */
export function lineageOf(t: SecretType | undefined): string {
  if (!t) return "";
  return `${t.what}. Comes from: ${t.origin}. Used by: ${t.usedBy}. Secured: ${t.securedBy}.`;
}

export async function openSecretForm(opts: SecretFormOpts): Promise<void> {
  let types: SecretType[];
  let existing: Secret | undefined;
  try {
    types = (await listSecretTypes()).filter((t) => t.named);
    if (opts.key) {
      existing = (await listSecrets()).find((s) => secretKey(s.kind, s.name) === opts.key);
    }
  } catch (e) {
    notify({ severity: "error", title: "Could not load the secret", message: String(e) });
    return;
  }
  if (opts.key && !existing) {
    notify({ severity: "error", title: "Not found", message: `No secret ${opts.key}.` });
    return;
  }
  const startKind = existing?.kind ?? opts.kind ?? types[0]?.kind ?? "";

  const items: FormItem[] = [
    {
      name: "kind",
      label: "Type",
      type: "select",
      required: true,
      disabled: Boolean(existing),
      options: types.map((t) => ({ value: t.kind, text: t.kind })),
      // The type's lineage, where it is read: under the field that chooses the type. A stored
      // secret shows its own type's; a new one shows each type's, since any may be chosen.
      helpText: existing
        ? lineageOf(types.find((t) => t.kind === existing.kind))
        : types.map((t) => `${t.kind}: ${lineageOf(t)}`).join(" — "),
    },
    {
      name: "name",
      label: "Name",
      type: "text",
      required: true,
      disabled: Boolean(existing),
      helpText: "What a channel or account names to use it: lowercase letters, digits and dashes.",
    },
    {
      name: "value",
      label: "Value",
      type: "password",
      required: !existing,
      helpText: existing
        ? "A value is stored (encrypted). Leave blank to keep it; enter a new one to rotate it."
        : "Stored encrypted; never shown again.",
    },
    {
      name: "description",
      label: "What it is",
      type: "text",
      required: true,
      helpText: 'Which one this is, e.g. "Slack incoming webhook for #ops-alerts".',
    },
    {
      name: "source",
      label: "Where it came from",
      type: "text",
      required: true,
      helpText: 'e.g. "Slack admin, app Continuum Alerts, Incoming Webhooks".',
    },
    {
      name: "hosts",
      label: "May be sent to",
      type: "text",
      helpText:
        "The hosts the value may be sent to, separated by commas. Blank takes the type's default: a webhook url's own host. An api key has no default.",
    },
  ];

  const used = existing && existing.usedBy.length > 0 ? ` — used by ${existing.usedBy.join(", ")}` : "";

  datasetFormRequest.next({
    title: existing ? `${secretKey(existing.kind, existing.name)}${used}` : "New secret",
    sections: [{ title: "", items }],
    initialValues: {
      kind: startKind,
      name: existing?.name ?? "",
      value: "",
      description: existing?.description ?? "",
      source: existing?.source ?? "",
      hosts: (existing?.hosts ?? []).join(", "),
    },
    submitLabel: "Save",
    onCancel: () => datasetFormRequest.next(undefined),
    onSubmit: async (values) => {
      const kind = String(values["kind"] ?? "");
      const name = String(values["name"] ?? "").trim();
      const res = await putSecret({
        kind,
        name,
        value: String(values["value"] ?? "").trim(),
        description: String(values["description"] ?? "").trim(),
        source: String(values["source"] ?? "").trim(),
        hosts: parseHosts(String(values["hosts"] ?? "")),
      });
      if (!res.success) {
        notify({ severity: "error", title: "Not saved", message: res.error || "save failed" });
        return;
      }
      notify({ severity: "success", title: "Saved", message: `Saved ${secretKey(kind, name)}` });
      if (opts.refreshView) ViewManager.get().refreshView(opts.refreshView);
      // Reopen on the stored secret: the value field is empty again and the label is as stored.
      await openSecretForm({ key: secretKey(kind, name), refreshView: opts.refreshView });
    },
  });
}
